import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/fsm';

const EquipmentCategories = () => {
  const { lang, t } = useLanguage();
  const f = t('fsm');
  const isRTL = lang === 'ar';

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({ name: '', name_ar: '', icon: '', description: '', sort_order: 0, default_checklist: [] });
  const [checklistInput, setChecklistInput] = useState('');
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const inputSt = {
    width: '100%', padding: '12px', border: '2px solid rgba(255,255,255,0.2)',
    borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box'
  };

  const labelSt = { display: 'block', fontSize: '12px', color: '#94a3b8', fontWeight: '600', marginBottom: '6px' };

  const sectionSt = {
    backgroundColor: 'rgba(26, 32, 56, 0.8)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px', padding: '16px'
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/equipment-categories?show_inactive=true`, { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } });
      const data = await res.json();
      if (data.success) setCategories(data.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchCategories(); }, []);

  const resetForm = () => {
    setFormData({ name: '', name_ar: '', icon: '', description: '', sort_order: 0, default_checklist: [] });
    setChecklistInput('');
    setEditId(null);
  };

  const handleEdit = (cat) => {
    const checklist = cat.default_checklist ? (typeof cat.default_checklist === 'string' ? JSON.parse(cat.default_checklist) : cat.default_checklist) : [];
    setFormData({
      name: cat.name, name_ar: cat.name_ar || '', icon: cat.icon || '',
      description: cat.description || '', sort_order: cat.sort_order || 0,
      default_checklist: checklist
    });
    setEditId(cat.id);
    setShowForm(true);
  };

  const addChecklistItem = () => {
    if (!checklistInput.trim()) return;
    setFormData(prev => ({ ...prev, default_checklist: [...prev.default_checklist, checklistInput.trim()] }));
    setChecklistInput('');
  };

  const removeChecklistItem = (index) => {
    setFormData(prev => ({ ...prev, default_checklist: prev.default_checklist.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = editId ? `${API}/equipment-categories/${editId}` : `${API}/equipment-categories`;
      const method = editId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) { setShowForm(false); resetForm(); fetchCategories(); }
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const toggleActive = async (cat) => {
    const token = localStorage.getItem('token');
    await fetch(`${API}/equipment-categories/${cat.id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: !cat.is_active })
    });
    fetchCategories();
  };

  return (
    <Layout>
    <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#f1f5f9', margin: 0 }}>📋 {f.equipment_categories || 'Equipment Categories'}</h1>
          <p style={{ fontSize: '13px', color: '#94a3b8', margin: '4px 0 0' }}>{f.manage_categories || 'Manage equipment types and default checklists'}</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} style={{
          backgroundColor: '#007bff', color: '#fff', border: 'none',
          padding: '9px 18px', borderRadius: '8px', fontWeight: '600', fontSize: '13px', cursor: 'pointer'
        }}>+ {f.add_category || 'Add Category'}</button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowForm(false)}>
          <div style={{ backgroundColor: '#151b30', borderRadius: '16px', padding: '24px', width: '550px', maxHeight: '85vh', overflow: 'auto', border: '1px solid rgba(255,255,255,0.1)', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}
            onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#f1f5f9', margin: '0 0 20px' }}>
              {editId ? `✏️ ${f.edit_category || 'Edit Category'}` : `➕ ${f.new_category || 'New Category'}`}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelSt}>{f.name_en || 'Name (English)'} *</label>
                  <input type="text" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} style={inputSt} required />
                </div>
                <div>
                  <label style={labelSt}>{f.name_ar || 'Name (Arabic)'}</label>
                  <input type="text" value={formData.name_ar} onChange={(e) => handleChange('name_ar', e.target.value)} style={{ ...inputSt, direction: 'rtl' }} />
                </div>
                <div>
                  <label style={labelSt}>{f.icon_emoji || 'Icon (Emoji)'}</label>
                  <input type="text" value={formData.icon} onChange={(e) => handleChange('icon', e.target.value)} style={inputSt} placeholder="❄️" />
                </div>
                <div>
                  <label style={labelSt}>{f.sort_order || 'Sort Order'}</label>
                  <input type="text" value={formData.sort_order} onChange={(e) => handleChange('sort_order', parseInt(e.target.value) || 0)} style={inputSt} />
                </div>
              </div>
              <div style={{ marginTop: '12px' }}>
                <label style={labelSt}>{f.description || 'Description'}</label>
                <input type="text" value={formData.description} onChange={(e) => handleChange('description', e.target.value)} style={inputSt} />
              </div>

              {/* Checklist Builder */}
              <div style={{ marginTop: '16px' }}>
                <label style={labelSt}>{f.default_checklist || 'Default Maintenance Checklist'}</label>
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                  <input type="text" value={checklistInput} onChange={e => setChecklistInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
                    style={{ ...inputSt, flex: 1 }} placeholder={f.add_checklist_item || 'Add checklist item...'} />
                  <button type="button" onClick={addChecklistItem} style={{
                    backgroundColor: 'rgba(0,123,255,0.2)', color: '#60a5fa', border: 'none',
                    padding: '8px 14px', borderRadius: '8px', fontWeight: '600', fontSize: '13px', cursor: 'pointer'
                  }}>{f.add || 'Add'}</button>
                </div>
                <div style={{ marginTop: '8px' }}>
                  {formData.default_checklist.map((item, i) => (
                    <div key={i} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '6px 10px', borderRadius: '6px', marginBottom: '4px',
                      backgroundColor: 'rgba(255,255,255,0.03)', fontSize: '13px', color: '#e2e8f0'
                    }}>
                      <span>☐ {item}</span>
                      <button type="button" onClick={() => removeChecklistItem(i)} style={{
                        background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '16px'
                      }}>×</button>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: isRTL ? 'flex-start' : 'flex-end', marginTop: '20px' }}>
                <button type="button" onClick={() => setShowForm(false)} style={{
                  backgroundColor: 'transparent', color: '#94a3b8', border: '2px solid rgba(255,255,255,0.15)',
                  padding: '8px 16px', borderRadius: '8px', fontWeight: '600', fontSize: '13px', cursor: 'pointer'
                }}>{f.cancel || 'Cancel'}</button>
                <button type="submit" disabled={saving} style={{
                  backgroundColor: saving ? '#6c757d' : '#007bff', color: '#fff', border: 'none',
                  padding: '8px 20px', borderRadius: '8px', fontWeight: '600', fontSize: '13px', cursor: 'pointer'
                }}>{saving ? (f.saving || 'Saving...') : (f.save || 'Save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Categories Grid */}
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>{f.loading || 'Loading...'}</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
          {categories.map(cat => {
            const checklist = cat.default_checklist ? (typeof cat.default_checklist === 'string' ? JSON.parse(cat.default_checklist) : cat.default_checklist) : [];
            return (
              <div key={cat.id} style={{ ...sectionSt, opacity: cat.is_active ? 1 : 0.5 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '28px' }}>{cat.icon}</span>
                    <div>
                      <div style={{ fontWeight: '700', color: '#f1f5f9', fontSize: '15px' }}>{isRTL && cat.name_ar ? cat.name_ar : cat.name}</div>
                      {cat.name_ar && !isRTL && <div style={{ fontSize: '12px', color: '#94a3b8' }}>{cat.name_ar}</div>}
                      {cat.name && isRTL && <div style={{ fontSize: '12px', color: '#94a3b8' }}>{cat.name}</div>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button onClick={() => handleEdit(cat)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px' }} title={f.edit || 'Edit'}>✏️</button>
                    <button onClick={() => toggleActive(cat)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px' }} title={cat.is_active ? (f.deactivate || 'Deactivate') : (f.activate || 'Activate')}>
                      {cat.is_active ? '🟢' : '🔴'}
                    </button>
                  </div>
                </div>
                {checklist.length > 0 && (
                  <div style={{ marginTop: '12px', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '10px' }}>
                    <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', marginBottom: '6px', textTransform: 'uppercase' }}>{f.checklist || 'Checklist'} ({checklist.length})</div>
                    {checklist.slice(0, 4).map((item, i) => (
                      <div key={i} style={{ fontSize: '12px', color: '#94a3b8', padding: '2px 0' }}>☐ {item}</div>
                    ))}
                    {checklist.length > 4 && <div style={{ fontSize: '12px', color: '#64748b' }}>+{checklist.length - 4} {f.more || 'more'}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
    </Layout>
  );
};

export default EquipmentCategories;